import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Linking } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import {
  EMPTY_GUEST_IDENTITY,
  GuestIdentityFields,
  validateGuestIdentity,
  type GuestIdentity,
  type GuestIdentityErrors,
} from './GuestIdentityFields';
import { SurveyAuthRequired } from './SurveyAuthRequired';
import { usePublicSurvey, useSubmitPublicSurveyResponse } from '../../hooks/usePublicSurvey';
import {
  parseSurveyCompletion,
  parseSurveyPages,
  resolveSurveyCompletionHtml,
} from '../../model/surveySchema';
import { clearSurveyDraft } from '../../state/surveyDrafts';
import { SurveyForm } from '../take/SurveyForm';
import { SurveyOutcomeMessage } from '../take/SurveyOutcomeMessage';
import { useSurveyScrollView } from '../take/useSurveyScrollView';

import { headerItem } from '@/components/HeaderActions';
import { Header } from '@/components/header';
import { Box } from '@/components/ui/box';
import { Spinner } from '@/components/ui/spinner';
import { VStack } from '@/components/ui/vstack';
import { MARKETING_SITE_URL } from '@/constants/links';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { useToaster } from '@/hooks/useToaster';
import { useGoBack } from '@/hooks/useGoBack';
import { Sentry } from '@/util/sentry';

/**
 * Public counterpart of the authenticated survey screen, for anonymous
 * guests. Like the authenticated screen, the route's `id` param is a survey
 * *group* id, resolved to whichever version is currently published via
 * `getPublicSurveyByGroupQueryOptions`, so a caller (e.g. an env var) can
 * hold a stable reference that keeps working across publishes. There is
 * also no per-guest de-dupe: the backend accepts a guest response keyed by
 * an email with no server-side check for a prior submission, so unlike the
 * secure screen this never shows an "already responded" state.
 *
 * The guest's full name, email and optional NPI are collected via dedicated
 * fields owned by this screen, not questions inside the survey schema itself —
 * a survey author has no reason to know the backend's guest-identity fields
 * need questions literally named `email` or `npi`, and the first real
 * published survey confirmed that assumption doesn't hold. They are also the
 * fields a returning guest retypes: answers are saved to the device as they
 * are written, but a name, email address and NPI are an identity rather than
 * an answer, and writing them to shared device storage for a week to save a
 * guest a few fields is a poor trade.
 *
 * A survey may also refuse anonymous answers entirely, in which case the
 * public endpoint rejects the fetch rather than returning a definition
 * carrying the flag, and this screen offers a sign-in step instead of the
 * form. The fetch is withheld from anyone holding a session, because a 401
 * on a live session force-signs-that-user-out — see the query below.
 */
export function PublicSurveyScreen() {
  const { id: surveyGroupId } = useLocalSearchParams<{ id: string }>();

  const { userId } = useAuthStatus();

  const router = useRouter();

  const goBack = useGoBack(ROUTES.CONTENT_LIBRARY);

  const { bottom } = useSafeAreaInsets();

  const toaster = useToaster();

  const [submitted, setSubmitted] = useState(false);

  const [emailMatchesAccount, setEmailMatchesAccount] = useState(false);

  /**
   * The survey's own thank-you, resolved from the answers actually sent.
   *
   * Held from submit time rather than recomputed on render, because the form
   * that produced those answers is gone by the time this shows.
   */
  const [completionHtml, setCompletionHtml] = useState<string | undefined>(undefined);

  /**
   * The thank-you the API chose for this survey's type. Guests are never
   * granted results, so the message is all this screen keeps from the submit.
   */
  const [completionMessage, setCompletionMessage] = useState<string | undefined>(undefined);

  const [identity, setIdentity] = useState<GuestIdentity>(EMPTY_GUEST_IDENTITY);

  const [identityErrors, setIdentityErrors] = useState<GuestIdentityErrors>({});

  /**
   * Withheld while a session exists. A signed-in visitor only ever sits on
   * this screen for the moment before the open layout forwards them to the
   * authenticated survey screen, and firing the public fetch in that window
   * would sign them out outright if the survey requires authentication: the
   * global response handler treats any 401 on a live session as an expired
   * session. Anonymous 401s are safe, since that handler ignores them.
   */
  const {
    data: survey,
    isLoading: isSurveyLoading,
    isError: isSurveyError,
    error: surveyError,
    requiresAuthentication: surveyRequiresAuthentication,
    isAuthRequiredError,
  } = usePublicSurvey(surveyGroupId, { enabled: !userId });

  /**
   * Held stable so the form's schema and visibility memos actually cache — they
   * key on the element array, and parsing inline would hand them a fresh one on
   * every render of this screen.
   */
  const surveyPages = useMemo(() => parseSurveyPages(survey?.schema), [survey?.schema]);

  /**
   * The form asks to scroll to the top when it moves between pages and when
   * validation blocks, because both replace what matters without moving the
   * scroll position — leaving the respondent mid-question on the next page, or
   * staring past an error summary they never see. The form decides when; the
   * scroll view belongs to this screen.
   */
  const { ScrollViewComponent, scrollToTop } = useSurveyScrollView();

  const { mutateAsync: submit, isPending: isSubmitting } = useSubmitPublicSurveyResponse(
    survey?.id
  );

  useEffect(() => {
    if (!isSurveyError) {
      return;
    }

    // An auth-required survey rejecting an anonymous fetch is this screen
    // working as designed, not a fault worth alerting on.
    if (isAuthRequiredError) {
      return;
    }

    Sentry.captureException(surveyError, {
      tags: { component: 'PublicSurveyScreen', source: 'survey' },
      extra: {
        surveyGroupId,
        errorMessage: surveyError instanceof Error ? surveyError.message : String(surveyError),
      },
    });
  }, [isSurveyError, surveyError, surveyGroupId]);

  const handleSubmit = async (answers: Record<string, unknown>) => {
    const validated = validateGuestIdentity(identity);

    if (!validated.success) {
      setIdentityErrors(validated.errors);

      return;
    }

    setIdentityErrors({});

    try {
      const submittedResponse = await submit({ guest: validated.data, answers });

      // Only once the response is accepted — a draft is what the user falls
      // back to when a submit fails, so discarding it any earlier would
      // destroy their answers at the exact moment they still need them.
      await clearSurveyDraft(undefined, surveyGroupId);

      setCompletionHtml(
        resolveSurveyCompletionHtml(parseSurveyCompletion(survey?.schema), answers)
      );

      setCompletionMessage(submittedResponse?.completion?.message);

      setEmailMatchesAccount(submittedResponse?.emailMatchesAccount === true);

      setSubmitted(true);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'PublicSurveyScreen', source: 'survey' },
        extra: {
          surveyGroupId,
          surveyDefinitionId: survey?.id,
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });

      toaster.showError({
        title: 'Unable to submit your response',
        apiError: { error },
      });
    }
  };

  const handleCancel = () => {
    goBack();
  };

  /**
   * Unlike Cancel, Done never goes back: back would land on the Content
   * Library gate, which re-asks the same attestation question the visitor
   * just declined to get here. This screen only ever holds a signed-out
   * visitor, since a signed-in one is forwarded to the secure survey screen.
   * A guest whose email already belongs to an account, as a login address or
   * an alias, goes to the directory, where they can sign in. Anyone else has
   * nothing to browse there, so they leave for the marketing site, in this tab
   * on web since the survey was the whole visit.
   */
  const handleDone = () => {
    if (emailMatchesAccount) {
      router.replace(ROUTES.DIRECTORY);

      return;
    }

    if (isWeb) {
      window.location.assign(MARKETING_SITE_URL);

      return;
    }

    Linking.openURL(MARKETING_SITE_URL).catch((error: Error) => {
      Sentry.captureException(error, {
        tags: { component: 'PublicSurveyScreen', source: 'survey-done-marketing-link' },
        extra: { errorMessage: error.message },
      });
    });
  };

  const needsAuthentication = surveyRequiresAuthentication || isAuthRequiredError;

  /**
   * A survey that turns anonymous visitors away is refused rather than
   * returned, so there is no name to put in the header on that path — the
   * only survey whose title is known here is one the backend was willing to
   * hand over. Falling back to a generic title keeps the header from
   * reading as a blank, half-loaded screen while someone is being asked to
   * sign in. Drop the fallback if the rejection ever starts carrying the
   * survey's name.
   */
  const headerTitle = survey?.name ?? (needsAuthentication ? 'Complete Survey' : '');

  /**
   * Whether the body is an end state rather than the form. The header's exit
   * reads "Close" there and leaves the way Done does: once a response is in
   * there is nothing left to cancel, and going back would land on the gate
   * that asked the visitor here.
   */
  const isShowingOutcome =
    !isSurveyLoading &&
    !userId &&
    (submitted || (!needsAuthentication && (isSurveyError || !survey)));

  let body: ReactNode = null;

  /** Only the form scrolls. Its end states are short and sit below the bar. */
  let bodyScrollsUnderHeader = false;

  // A signed-in visitor never resolves a survey here — the query is withheld
  // and the open layout is mid-forward to the authenticated screen — so hold
  // the spinner rather than falling through to "isn't available".
  if (isSurveyLoading || !!userId) {
    body = <Spinner size="large" className="flex-1" />;
  } else if (submitted) {
    body = (
      <SurveyOutcomeMessage
        message={completionMessage ?? 'Thanks for your feedback!'}
        html={completionHtml}
        onDone={handleDone}
      />
    );
  } else if (needsAuthentication) {
    body = <SurveyAuthRequired surveyGroupId={surveyGroupId} />;
  } else if (isSurveyError || !survey) {
    body = (
      <SurveyOutcomeMessage message="This survey isn't available right now." onDone={handleDone} />
    );
  } else {
    bodyScrollsUnderHeader = true;

    body = (
      <KeyboardAwareScrollView
        mode="layout"
        contentInsetAdjustmentBehavior="automatic"
        ScrollViewComponent={ScrollViewComponent}
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 32 }}
        /*
         * Deliberately stricter than the rest of the app, which persists taps.
         *
         * A tap that lands outside the focused field dismisses the keyboard and
         * stops there, rather than also being delivered to whatever it landed
         * on. A survey page is mostly radio and checkbox rows sized to the 48px
         * touch-target minimum, so their pressable area reaches into what looks
         * like blank space; persisting the tap let a respondent putting the
         * keyboard away silently overwrite an answer they had already given.
         *
         * The cost is one extra tap to answer a question straight after typing.
         * That is the right trade here: these answers are analyzed and the
         * respondent is paid for them, so a wrong one is worse than a slow one.
         */
        keyboardShouldPersistTaps="never"
        keyboardDismissMode="on-drag"
        bottomOffset={18}>
        <VStack className="mx-auto w-full max-w-screen-md px-6 pt-2" space="md">
          <GuestIdentityFields
            value={identity}
            errors={identityErrors}
            onChange={(field, value) => {
              setIdentity((current) => ({ ...current, [field]: value }));

              setIdentityErrors((current) => ({ ...current, [field]: undefined }));
            }}
          />

          <SurveyForm
            surveyId={survey.id}
            surveyName={survey.name ?? ''}
            pages={surveyPages}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onScrollToTop={scrollToTop}
            surveyGroupId={surveyGroupId}
            surveyType={survey.type}
          />
        </VStack>
      </KeyboardAwareScrollView>
    );
  }

  return (
    <Box className="flex-1 bg-background-0" style={{ paddingBottom: bottom }}>
      <Header
        headerShown
        coversActingAsBanner
        contentScrollsUnderHeader={bodyScrollsUnderHeader}
        title={headerTitle}
        leftItems={[
          isShowingOutcome ? headerItem.close(handleDone) : headerItem.cancel(handleCancel),
        ]}
      />

      <VStack className="w-full flex-1">{body}</VStack>
    </Box>
  );
}
